import React from 'react';

import { observer } from 'mobx-react-lite';
import TCClient from '../../../../../client';
import { CtaButton } from '../../../../shared-components/CtaButton/CtaButton';
import { ViewContainer } from '../../../../shared-components/View/ViewContainer/ViewContainer';
import { useAdminFeatureFlag } from 'admin/hooks/useAdminFeatureFlag';
import { actions, useAppDispatch, useAppSelector } from 'redux-stores';

type RemoveUserProps = {
  onClose: () => void;
};

const { setUsersPageView } = actions;

// observer is required so the component re-renders when MobX featureFlags/adminFeatureFlags change
const RemoveUser: React.FC<RemoveUserProps> = observer(({ onClose }) => {
  const { selectedUser, selectedOrganization } = useAppSelector((state) => ({
    selectedUser: state.admin.selectedUser,
    selectedOrganization: state.admin.selectedOrganization,
  }));

  const { ENABLE_US_SCIM_DELETION } = useAdminFeatureFlag(['ENABLE_US_SCIM_DELETION']);

  const dispatch = useAppDispatch();

  const removeUser = async () => {
    const confirm = await window.confirm('Are you sure you want to permanently remove this user?');
    if (confirm) {
      const { token: orgId } = selectedOrganization;
      const { id: userId } = selectedUser;

      const deleteUserFn = ENABLE_US_SCIM_DELETION
        ? async (userId: string): Promise<{ type: string }> =>
            (await TCClient.scim.deleteScimUser(orgId, userId)) as { type: string }
        : async (userId: string): Promise<{ type: string }> =>
            (await TCClient.adminUsers.organizationLeave({
              orgId,
              userId,
            })) as { type: string };

      try {
        await deleteUserFn(userId);
        dispatch(setUsersPageView({ selectedUsersPageView: 'users' }));
      } catch (error) {
        console.error('Could not remove user', error);
      }
    }
  };

  return (
    <ViewContainer
      header={'Remove User'}
      subHeader={
        'Permanently remove this user from your organization. Changes will take effect immediately.'
      }
      onClose={onClose}
    >
      <div>
        <CtaButton
          label={'Remove User'}
          primary
          size={'small'}
          onClick={async () => {
            await removeUser();
          }}
        />
      </div>
    </ViewContainer>
  );
});

export default RemoveUser;
